/**
 * Textos de paneles y flujos de administración.
 */
// --- Admin: mantenimiento de usuarios ---
export const adminUsers = {
  usersPageTitle: 'Gestión de Usuarios',
  usersPageSubtitle: 'Administra los usuarios del sistema',
  createUserButton: 'Crear Usuario',
  resetAllAiUsageButton: 'Resetear IA del mes',
  createUserSuccess: 'Usuario creado exitosamente',
  createUserError: 'Error al crear usuario',
  updateUserSuccess: 'Usuario actualizado exitosamente',
  updateUserError: 'Error al actualizar usuario',
  toggleStatusSuccess: 'Estado del usuario actualizado exitosamente',
  toggleStatusError: 'Error al cambiar estado del usuario',
  resetPasswordSuccess: 'Contraseña restablecida exitosamente',
  resetPasswordError: 'Error al restablecer contraseña', // pragma: allowlist secret
  verifyUserError: 'Error al verificar usuario',
  blacklistSuccess: 'Usuario agregado a la lista negra',
  blacklistError: 'Error al agregar a lista negra',
  unblacklistSuccess: 'Usuario removido de la lista negra',
  unblacklistError: 'Error al remover de lista negra',
  deleteUserSuccess: 'Usuario eliminado correctamente',
  deleteUserError: 'Error al eliminar usuario',
  resetAiUsageTitle: 'Resetear uso IA (mes actual)',
  resetAiUsageConfirmTitle: 'Confirmar reseteo de uso IA',
  resetAiUsageConfirmMessage: '¿Está seguro que desea resetear el uso de IA del mes actual para este usuario?',
  resetAiUsageConfirmCancel: 'Cancelar',
  resetAiUsageConfirmButton: 'Sí, resetear',
  resetAiUsageConfirmLoading: 'Reseteando...',
  resetAiUsageSuccess: 'Uso de IA del mes actual reseteado.',
  resetAiUsageError: 'Error al resetear uso de IA.',
  resetAllAiUsageSuccess: 'IA del mes reseteada para todos los usuarios.',
  resetAllAiUsageError: 'Error al resetear IA del mes.',
} as const;

// --- Admin: panel de acciones (tarjetas del dashboard admin) ---
export const adminDashboard = {
  title: 'Acciones de Administración',
  subtitle: 'Gestiona el sistema y comunícate con los usuarios',
  notificationsTitle: 'Notificaciones Masivas',
  notificationsDesc: 'Envío de comunicados a usuarios',
  clinicsTitle: 'Gestión de Clínicas',
  clinicsDesc: 'Crear y administrar clínicas veterinarias',
  petClinicsTitle: 'Asignar mascotas a clínicas',
  petClinicsDesc: 'Controla qué clínica tiene acceso a cada ficha de paciente.',
  petsManagementTitle: 'Gestión de Mascotas',
  petsManagementDesc: 'Busca y gestiona todas las mascotas del sistema.',
  usersTitle: 'Gestión de Usuarios',
  usersDesc: 'Gestionar usuarios y roles de clínica',
  userPlansTitle: 'Planes por usuario',
  userPlansDesc: 'Asignar o cambiar plan y extender prueba gratis',
  userAuditTitle: 'Auditoría de usuarios',
  userAuditDesc: 'Ver qué cambios se han hecho sobre los usuarios',
  appointmentsTitle: 'Gestión de Citas',
  appointmentsDesc: 'Revisa, agenda y controla las citas médicas.',
  systemSettingsTitle: 'Configuración del Sistema',
  systemSettingsDesc: 'Ajusta parámetros globales y políticas de seguridad.',
  maintenanceTitle: 'Mantenimiento de base de datos',
  maintenanceDesc: 'VACUUM/ANALYZE en tablas de alto uso para evitar lentitud.',
  reportsTitle: 'Reportes Avanzados',
  reportsDesc: 'Consulta estadísticas financieras y operativas.',
  inventoryTitle: 'Inventario Clínico',
  inventoryDesc: 'Controla insumos, vacunas y medicamentos disponibles.',
  emailsTitle: 'Gestión de Correos',
  emailsDesc: 'Configura y gestiona los correos electrónicos automáticos del sistema.',
  suggestionsTitle: 'Calificaciones y Sugerencias',
  suggestionsDesc: 'Revisa las calificaciones de la app y sugerencias de los usuarios',
  paymentMethodsTitle: 'Gestión de Métodos de Pago',
  paymentMethodsDesc: 'Configura las credenciales de MercadoPago, Transbank y Khipu.',
  paymentApprovalsTitle: 'Aprobaciones de Pagos',
  paymentApprovalsDesc: 'Revisa y aprueba pagos manuales de Khipu.',
  analyticsTitle: 'Análisis y Proyecciones',
  analyticsDesc: 'Visualización de crecimiento de usuarios, suscripciones e ingresos',
  veripagoTitle: 'VeriPago',
  veripagoDesc: 'Sube una imagen de comprobante y obtén el porcentaje de posible manipulación',
  privacyRequestsTitle: 'Solicitudes de Privacidad (Ley 21.719)',
  privacyRequestsDesc: 'Gestiona solicitudes ARCOP+ de los titulares de datos',
} as const;

export const privacyAdminPage = {
  title: 'Solicitudes de Privacidad',
  subtitle: 'Derechos ARCOP+ — Ley N° 21.719 (plazo legal: 30 días corridos)',
  back: 'Volver al panel de administración',
  searchPlaceholder: 'Buscar por email, nombre o descripción…',
  refresh: 'Actualizar',
  empty: 'No hay solicitudes con los filtros seleccionados.',
  loadError: 'Error al cargar solicitudes de privacidad',
  updateSuccess: 'Solicitud actualizada',
  updateError: 'Error al actualizar la solicitud',
  statusLabel: 'Estado',
  notesLabel: 'Notas internas (admin)',
  notesPlaceholder: 'Respuesta o seguimiento interno…',
  filterAll: 'Todos los estados',
  statPending: 'Pendientes',
  statInProgress: 'En proceso',
  statCompleted: 'Completadas',
  statRejected: 'Rechazadas',
  manage: 'Gestionar',
  requestTitle: (id: number, type: string) => `Solicitud #${id} — ${type}`,
  saving: 'Guardando…',
  save: 'Guardar',
} as const;

export const privacyRequestStatusLabels = {
  pending: 'Pendiente',
  in_progress: 'En proceso',
  completed: 'Completada',
  rejected: 'Rechazada',
} as const;

export const privacyRequestTypeLabels = {
  access: 'Acceso',
  rectification: 'Rectificación',
  cancellation: 'Cancelación',
  opposition: 'Oposición',
  portability: 'Portabilidad',
  blocking: 'Bloqueo',
} as const;

// --- Admin: VeriPago ---
export const veripagoPage = {
  title: 'VeriPago',
  subtitle: 'Sube una imagen o PDF de comprobante y obtén el porcentaje de posible manipulación.',
  back: 'Volver al panel de administración',
  uploadLabel: 'Archivo (imagen o PDF)',
  chooseFile: 'Elige un archivo',
  analyze: 'Analizar',
  analyzing: 'Analizando…',
  resultLabel: 'Porcentaje de manipulación',
  selectFile: 'Selecciona un archivo.',
  invalidType: 'Tipo de archivo no permitido. Usa JPEG, PNG, WebP o PDF.',
  tooBig: (mb: number) => `El archivo no puede superar ${mb} MB.`,
  error: 'Error al analizar. Intenta de nuevo.',
} as const;

export const adminMaintenance = {
  backToAdmin: 'Volver al panel de administración',
  pageTitle: 'Mantenimiento de base de datos',
  pageSubtitle: 'Tablas sugeridas para VACUUM/ANALYZE. Ejecutar de forma periódica ayuda a evitar lentitud.',
  refreshList: 'Actualizar lista',
  logPurgeTitle: 'Limpieza de tablas de log',
  logPurgeSubtitle: (days: number) =>
    `Borrar registros de auditoría, errores y webhooks con más de ${days} días. Después se ejecuta VACUUM.`,
  logPurgeConfigHint: 'El número de días se configura en Configuración del sistema (Mantenimiento → log_purge_retention_days).',
  logPurgeCutoffLabel: 'Se borran registros con fecha anterior a:',
  logPurgeCutoffUsed: 'Corte usado:',
  logPurgeNoRecords: (days: number) =>
    `No había registros con más de ${days} días. Se ejecutó VACUUM.`,
  refresh: 'Actualizar',
} as const;

// --- Admin: gestión de mascotas (panel admin) ---
export const adminPets = {
  title: 'Gestión de Mascotas',
  subtitle: 'Busca y gestiona todas las mascotas del sistema',
  searchPlaceholder: 'Buscar por nombre, especie, dueño, microchip, raza…',
  searchLabel: 'Buscar mascota',
  includeInactive: 'Incluir eliminadas',
  clearFilters: 'Limpiar filtros',
  loadError: 'Error al cargar mascotas',
  info: 'Información',
  owner: 'Dueño',
  rainbow: 'Arcoíris',
  viewDetails: 'Ver detalles',
  totalPets: 'Total de mascotas en el sistema:',
  includeInactiveHint: 'Activa el checkbox "Incluir eliminadas" para ver todas las mascotas',
  retry: 'Reintentar',
} as const;

// --- Admin: toasts (dashboard, menús) ---
export const adminToasts = {
  userDeleted: 'Usuario eliminado exitosamente',
  menuConfigUpdated: 'Configuración de menús actualizada',
  menuConfigUpdateError: 'No se pudo actualizar la configuración de menús. Inténtalo nuevamente.',
} as const;

// --- Admin: página dashboard ---
export const adminDashboardPage = {
  refreshStatsTitle: 'Actualizar estadísticas',
  statsError: 'Error al cargar estadísticas',
  statsErrorDetail: 'No se pudieron cargar las estadísticas del sistema. Intenta de nuevo.',
  pageTitle: 'Panel de Administración - Mi Huellita Digital',
  pageSubtitle: 'Gestiona usuarios, revisa auditorías y administra el sistema.',
  retry: 'Reintentar',
} as const;

// --- Admin: asignación mascotas-clínicas ---
export const adminPetClinics = {
  updateError: 'No fue posible actualizar la asignación. Intenta nuevamente.',
  searchPlaceholder: 'Buscar por nombre de mascota, microchip o dueño...',
  pageTitle: 'Asignación de mascotas a clínicas',
  pageSubtitle: 'Gestiona qué clínica puede acceder y seguir la ficha de cada mascota.',
  resultTitle: 'Resultado de mascotas',
  selectClinicHint: 'Selecciona una clínica para asignarla. Dejar en blanco para quitar.',
  clinicAssigned: 'Clínica asignada',
  unassigned: 'Sin asignar',
  removeAssignment: 'Quitar asignación',
  noPetsFound: 'No se encontraron mascotas con los filtros actuales.',
  searchButton: 'Buscar',
  clearButton: 'Limpiar',
  back: 'Volver',
} as const;

// --- Admin: planes por usuario ---
export const adminUserPlans = {
  extendError: 'No se pudo extender la prueba.',
  extendSuccessDays: 'Prueba extendida +{days} días.',
  bulkExtendError: 'Error al extender prueba masivamente.',
  bulkExtendSuccess: 'Prueba extendida a todos.',
  planUpdated: 'Plan actualizado.',
  planChangeError: 'No se pudo cambiar el plan.',
  reduceError: 'No se pudieron restar los días.',
  reduceSuccessDays: 'Se restaron {days} días de la prueba.',
  searchPlaceholder: 'Buscar por nombre o email...',
  pageTitle: 'Planes por usuario',
  pageSubtitle: 'Asignar o cambiar plan y extender prueba gratis por usuario o masivamente.',
  extendAll: 'Extender prueba a todos',
  searchLabel: 'Buscar por nombre o email',
  planType: 'Tipo de plan',
  freeDays: 'Días gratis',
  extendTrial: 'Extender prueba',
  reduceDays: 'Restar días',
  changePlan: 'Cambiar plan',
  back: 'Volver',
  userColumn: 'Usuario',
  roleColumn: 'Rol',
  actionsColumn: 'Acciones',
} as const;
